import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Divider, Form, Header, Icon, SemanticICONS } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import Dialog from '~/components/Dialog'
import ConsultantFields from './ConsultantFields'
import { StyledFileSection } from '../style'
import { getFileIcon, getFileTypeLabel } from '../utils'
import ErrorIcon from '~/components/ErrorIcon'
import FileUpload from '../FileUpload'
import { b64toBlob } from '~/utils/helpers'

const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/gif': ['.gif'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg']
}
const FILE_MIME_TYPES = ['xlsx', 'pdf', 'gif', 'png', 'jpg', 'jpeg', 'PDF']
const MAX_FILE_SIZE_MB = 10
const SUPPORTED_FORMATS_TEXT = 'PDF, XLSX, GIF, PNG, JPG'
const consultantFieldsKeys = ['consultantLastName', 'consultantFirstName', 'consultantPhoneNumber', 'consultantEmail', 'consultantCompanyName']

const FeedingPlanSection: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const { firstColor } = useContext(ThemeContext)

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [fileSrc, setFileSrc] = useState(null)

  const watchSelfCalculatedFeedingPlan = useWatch({ control, name: 'selfCalculatedFeedingPlan' })
  const watchFeedingPlanFile = useWatch({ control, name: 'feedingPlanFile' })

  const handleFileChange = (fileData, fileName) => {
    setValue('feedingPlanFile', { data: fileData, name: fileName })
  }

  const isCorrectPreviewType = (fileName: string) => {
    return FILE_MIME_TYPES
      .filter(mime => mime !== 'xlsx')
      .some(ext => fileName?.toLowerCase().endsWith(`.${ext}`))
  }

  const handlePreviewFile = () => {
    if (isCorrectPreviewType(watchFeedingPlanFile?.name)) {
      const type = watchFeedingPlanFile?.data?.split(';')[0]?.split('data:')[1]
      const blob = b64toBlob(watchFeedingPlanFile.data, type);
      setFileSrc(URL.createObjectURL(blob))
      setIsPreviewOpen(true)
    } else {
      const link = document.createElement('a')
      link.href = watchFeedingPlanFile.data
      link.download = watchFeedingPlanFile.name
      link.click()
    }
  }

  const handleClosePreview = () => {
    setIsPreviewOpen(false)
    setFileSrc(null)
  }

  return (
    <>
      <Header as='h4'>{t('Feeding Plan')}</Header>
      <Divider />
      <Controller
        control={control}
        name='selfCalculatedFeedingPlan'
        rules={{ required: true }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {t('I create and calculate my feeding plan myself')}
              <ErrorIcon control={control} name='selfCalculatedFeedingPlan' />
            </label>
            <div style={{ display: 'flex', gap: 20 }}>
              <Checkbox
                radio
                label={t('Yes')}
                checked={watchSelfCalculatedFeedingPlan === 1}
                onClick={() => setValue('selfCalculatedFeedingPlan', 1)}
              />
              <Checkbox
                radio
                label={t('No')}
                checked={watchSelfCalculatedFeedingPlan === 0}
                onClick={() => {
                  setValue('selfCalculatedFeedingPlan', 0)
                  consultantFieldsKeys.map(key => setValue(key, ''))
                }}
              />
            </div>
          </Form.Field>
        )}
      />

      {watchSelfCalculatedFeedingPlan === 0 && <ConsultantFields />}

      <Form.Field style={{ marginTop: '1.5em' }}>
        <label>{t('Feeding plan file (optional)')}</label>
        <StyledFileSection $primaryColor={firstColor}>
          <div className='file-icon'>
            <Icon name={getFileIcon(watchFeedingPlanFile?.name) as SemanticICONS} size='large' />
          </div>
          <div className='file-info'>
            <div className='file-label'>{getFileTypeLabel(watchFeedingPlanFile?.name)}</div>
            {watchFeedingPlanFile?.name ? (
              <div className='file-name' onClick={handlePreviewFile}>
                {watchFeedingPlanFile.name}
              </div>
            ) : (
              <div className='file-placeholder'>{t('No file selected')}</div>
            )}
          </div>
          <div className='file-actions'>
            <FileUpload
              button
              encodeFiles
              accept={ACCEPTED_FILE_TYPES}
              mimeTypes={FILE_MIME_TYPES}
              sizeLimitInMB={MAX_FILE_SIZE_MB}
              onChange={handleFileChange}
            />
          </div>
        </StyledFileSection>
        <small style={{ color: 'gray', marginTop: '0.5em', display: 'block' }}>
          {t('Supported formats')}: {SUPPORTED_FORMATS_TEXT}. {t('Max size')}: {MAX_FILE_SIZE_MB}MB
        </small>
      </Form.Field>

      <Dialog
        type='preview'
        textHeader={t('Feeding Plan Preview')}
        heightContent='90%'
        heightDialog='80%'
        isDialogOpen={isPreviewOpen}
        noScrollingContent={false}
        style={{ width: '70%' }}
        textClose={t('Close')}
        handleClose={handleClosePreview}
        textContent={
          <div style={{ position: 'relative', width: '100%', minHeight: '500px', height: '93%' }}>
            {watchFeedingPlanFile?.name && /\.(gif|png|jpe?g)$/i.test(watchFeedingPlanFile.name) ? (
              <img
                src={fileSrc}
                alt={watchFeedingPlanFile.name}
                style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', margin: '0 auto' }}
              />
            ) : (
              <iframe
                src={watchFeedingPlanFile?.data ? `${fileSrc}#toolbar=1&navpanes=0&view=FitH` : 'about:blank'}
                width='100%'
                height='100%'
                style={{ minHeight: 500 }}
              />
            )}
          </div>
        }
      />
    </>
  )
}

export default FeedingPlanSection
